import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../api/client';
import { authAPI } from '../api/auth';
import { useAuthStore } from '../store/auth';
import { Button } from '../components/ui/Button';

function BackIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 12H5M12 5l-7 7 7 7" />
    </svg>
  );
}

export function Register() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const refFromUrl = (searchParams.get('ref') ?? searchParams.get('invite_code') ?? '').trim();
  const zoneFromUrl = (searchParams.get('position') ?? searchParams.get('zone') ?? '').trim().toUpperCase();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [sponsor, setSponsor] = useState(refFromUrl);
  const [position, setPosition] = useState<'L' | 'R'>(() =>
    zoneFromUrl === 'R' || zoneFromUrl === '右' || zoneFromUrl === 'RIGHT' ? 'R' : 'L',
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const setTokens = useAuthStore((s) => s.setTokens);
  const setProfile = useAuthStore((s) => s.setProfile);
  const navigate = useNavigate();

  async function handleRegister(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const tokens = await authAPI.register({
        username,
        password,
        password_confirm: passwordConfirm,
        invite_code: sponsor.trim(),
        position,
      });
      setTokens(tokens.access_token, tokens.refresh_token);
      const profile = await authAPI.me();
      setProfile(profile as never);
      navigate('/wallet', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('auth.registerFailed', 'Registration failed'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ background: '#010713' }}>
      <div className="relative mx-auto w-full max-w-[430px] min-h-screen">
        <div className="absolute inset-0 -z-10 bg-uo-hero bg-cover bg-top opacity-20" />

        <div className="relative z-20 flex items-center justify-between px-4 pt-3">
          <Link
            to="/login"
            className="flex items-center gap-1.5 rounded-full border border-white/15 bg-white/8 px-3 py-1.5 text-xs text-white/70 backdrop-blur-sm"
            aria-label={t('common.back', 'Back')}
          >
            <BackIcon />
            <span>{t('common.back', 'Back')}</span>
          </Link>

          <span className="text-[13px] font-semibold text-white/80">
            {t('auth.registerAccount', 'Register Account')}
          </span>

          <div className="w-[72px]" />
        </div>

        <div className="relative z-10 px-5 pt-8 pb-4">
          <h1 className="text-2xl font-bold text-white">
            {t('auth.createAccount', 'Create Account')}
          </h1>
          <p className="mt-1 text-sm text-white/55">
            {t('auth.registerSubtitle', '填写账户名、密码，并选择旗下安置位置')}
          </p>
        </div>

        <div className="relative z-20 px-5 pb-10">
          <form onSubmit={handleRegister} className="space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-white/55">
                {t('auth.accountName', '账户名')} <span className="text-negative">*</span>
              </label>
              <input
                className="h-[56px] w-full rounded-full border border-white/12 bg-black/25 px-5 text-sm text-white placeholder:text-white/40 outline-none backdrop-blur-md focus:ring-2 focus:ring-brand-500/25"
                placeholder={t('auth.placeholderUsername', '请输入账户名')}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoCapitalize="none"
                autoCorrect="off"
                required
              />
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-medium text-white/55">
                {t('auth.passwordHint', 'Password (upper+lower+digit, ≥8 chars)')} <span className="text-negative">*</span>
              </label>
              <input
                type="password"
                className="h-[56px] w-full rounded-full border border-white/12 bg-black/25 px-5 text-sm text-white placeholder:text-white/40 outline-none backdrop-blur-md focus:ring-2 focus:ring-brand-500/25"
                placeholder={t('auth.placeholderPassword', '请输入密码')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-medium text-white/55">
                {t('auth.confirmPassword', 'Confirm Password')} <span className="text-negative">*</span>
              </label>
              <input
                type="password"
                className="h-[56px] w-full rounded-full border border-white/12 bg-black/25 px-5 text-sm text-white placeholder:text-white/40 outline-none backdrop-blur-md focus:ring-2 focus:ring-brand-500/25"
                placeholder={t('auth.confirmPassword', 'Confirm Password')}
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                required
              />
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-medium text-white/55">
                {t('auth.sponsor', '旗下推荐人')} <span className="text-negative">*</span>
              </label>
              <input
                className="h-[56px] w-full rounded-full border border-white/12 bg-black/25 px-5 text-sm text-white placeholder:text-white/40 outline-none backdrop-blur-md focus:ring-2 focus:ring-brand-500/25"
                placeholder={t('auth.sponsorPlaceholder', '推荐人账户名或邀请码')}
                value={sponsor}
                onChange={(e) => setSponsor(e.target.value)}
                autoCapitalize="none"
                autoCorrect="off"
                required
              />
            </div>

            <div>
              <div className="mb-2 text-xs font-medium text-white/55">
                {t('auth.position', 'Placement zone')} <span className="text-negative">*</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {(['L', 'R'] as const).map((p) => {
                  const active = position === p;
                  const label = p === 'L' ? t('auth.positionLeft', '左区') : t('auth.positionRight', '右区');
                  return (
                    <label
                      key={p}
                      className={[
                        'flex cursor-pointer flex-col items-center justify-center rounded-2xl border px-3 py-4 text-sm font-semibold transition-all active:scale-95',
                        active
                          ? 'border-brand-500/50 bg-brand-500/10 text-brand-300'
                          : 'border-white/12 bg-black/25 text-white/55',
                      ].join(' ')}
                    >
                      <input
                        type="radio"
                        name="pos"
                        className="sr-only"
                        checked={active}
                        onChange={() => setPosition(p)}
                      />
                      <span>{label}</span>
                      <span className="mt-1 text-[10px] font-normal opacity-70">{p}</span>
                    </label>
                  );
                })}
              </div>
              <p className="mt-2 text-[10px] leading-snug text-white/45">
                {t('auth.positionHint', 'Choose left or right zone under your referrer\'s binary tree.')}
              </p>
            </div>

            {error && (
              <div className="rounded-xl border border-negative/30 bg-negative/10 px-4 py-2.5 text-sm text-negative">
                {error}
              </div>
            )}

            <Button type="submit" fullWidth size="lg" isLoading={loading} className="rounded-full py-4 text-base">
              {loading ? t('auth.loggingIn', 'Processing…') : t('auth.registerBtn', 'Register')}
            </Button>
          </form>

          <p className="mt-6 text-center text-xs text-white/50">
            {t('auth.hasAccount', 'Already have an account?')}{' '}
            <Link to="/login" className="font-semibold text-brand-300 hover:text-brand-200">
              {t('auth.loginBtn', 'Login')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
